import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, CircularProgress, InputAdornment, IconButton, Tooltip, Typography, ButtonBase
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '../../context/ToastContext';
import {
  fetchBorrowTransactions,
  fetchLibraryItems,
  fetchLibrarySettings,
  borrowBook,
  returnBook,
  searchBorrowers
} from '../../slices/librarySlice';
import SearchableSelect from '../SearchableSelect';
import {
  C, FONT, TONES, inputSx, primaryButtonSx, dialogPaperProps, dialogTitleSx,
  BookCover, Pill, FilterChips, EmptyState, formatDate, isOverdue
} from './libraryUi';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/PrintOutlined';
import SchoolIcon from '@mui/icons-material/SchoolOutlined';
import BadgeIcon from '@mui/icons-material/BadgeOutlined';
import PersonIcon from '@mui/icons-material/PersonOutline';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';

// Borrower who is not a student or employee - every field is optional
const EMPTY_GUEST = { name: '', phone: '', id_number: '' };

const BORROWER_TYPES = [
  { value: 'student', label: 'طالب', icon: <SchoolIcon /> },
  { value: 'employee', label: 'موظف', icon: <BadgeIcon /> },
  { value: 'other', label: 'آخر', icon: <PersonIcon /> },
];

const MEMBER_TYPE_LABELS = { student: 'طالب', employee: 'موظف', other: 'آخر' };

const DAY = 24 * 60 * 60 * 1000;

// Names and titles are free text - escape them before writing the print HTML
const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (ch) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]
));

// "Days left" / "days late" / "returned" for one borrow
const timeStatus = (t) => {
  if (t.status === 'returned') return { label: `أُعيد ${formatDate(t.returned_at)}`, tone: 'success' };
  if (t.status === 'lost') return { label: 'مفقود', tone: 'neutral' };
  const today = new Date(new Date().toDateString());
  const days = Math.round((new Date(new Date(t.due_date).toDateString()) - today) / DAY);
  if (days < 0) return { label: `متأخر ${-days} يوم`, tone: 'danger' };
  if (days === 0) return { label: 'يُعاد اليوم', tone: 'warning' };
  return { label: `باقي ${days} ${days === 1 ? 'يوم' : 'أيام'}`, tone: days <= 2 ? 'warning' : 'primary' };
};

const BorrowRow = ({ transaction: t, onReturn, saving }) => {
  const time = timeStatus(t);
  const fine = Number(t.fine_amount || 0);
  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 2, p: 1.5, pl: 2,
        bgcolor: '#fff', borderRadius: 3.5, border: `1px solid ${C.border}`,
        borderRight: `4px solid ${TONES[time.tone].fg}`,
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
      }}
    >
      <BookCover title={t.copy_title} width={38} />
      <Box flex={1} minWidth={160}>
        <Typography fontWeight={800} color={C.text} noWrap sx={{ fontFamily: FONT }}>
          {t.copy_title || '-'}
        </Typography>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Typography variant="body2" color={C.text} sx={{ fontFamily: FONT }}>
            {t.member_name || '-'}
          </Typography>
          {t.member_type && t.member_type !== 'student' && (
            <Pill label={MEMBER_TYPE_LABELS[t.member_type] || t.member_type} />
          )}
          {t.member_phone && (
            <Typography variant="caption" color={C.muted} sx={{ direction: 'ltr' }}>
              {t.member_phone}
            </Typography>
          )}
        </Box>
      </Box>
      <Box sx={{ minWidth: 150, display: { xs: 'none', md: 'block' } }}>
        <Typography variant="caption" color={C.muted} display="block" sx={{ fontFamily: FONT }}>
          استعارة {formatDate(t.borrowed_at)}
        </Typography>
        <Typography variant="caption" color={C.muted} display="block" sx={{ fontFamily: FONT }}>
          إرجاع حتى {formatDate(t.due_date)}
        </Typography>
      </Box>
      <Box display="flex" alignItems="center" gap={1} flexShrink={0} sx={{ mr: { xs: 'auto', sm: 0 } }}>
        <Pill label={time.label} tone={time.tone} />
        {fine > 0 && <Pill label={`غرامة ${fine}`} tone="danger" />}
      </Box>
      <Box sx={{ width: 96, flexShrink: 0, textAlign: 'left' }}>
        {t.status === 'borrowed' && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<KeyboardReturnIcon />}
            onClick={() => onReturn(t.id)}
            disabled={saving}
            sx={{
              borderRadius: 2.5, fontFamily: FONT, fontWeight: 700,
              borderColor: C.border, color: C.text,
              '&:hover': { borderColor: C.success, color: C.success, bgcolor: C.successSoft },
            }}
          >
            إرجاع
          </Button>
        )}
      </Box>
    </Box>
  );
};

const LibraryBorrowingTab = () => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { transactions, items, settings, loading, saving } = useSelector((state) => state.library);
  const [borrowDialogOpen, setBorrowDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState('');
  const [borrowerType, setBorrowerType] = useState('student');
  const [borrowers, setBorrowers] = useState([]);
  const [selectedBorrower, setSelectedBorrower] = useState('');
  const [guest, setGuest] = useState(EMPTY_GUEST);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('borrowed');
  const [dateFilter, setDateFilter] = useState('all');

  const refresh = () => {
    dispatch(fetchBorrowTransactions());
    dispatch(fetchLibraryItems());
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Load the students / employees list when the dialog opens or the type changes
  useEffect(() => {
    if (!borrowDialogOpen) return;
    setSelectedBorrower('');
    if (borrowerType === 'other') return;
    searchBorrowers(borrowerType)
      .then(setBorrowers)
      .catch(() => setBorrowers([]));
  }, [borrowDialogOpen, borrowerType]);

  useEffect(() => {
    if (borrowDialogOpen && !settings) dispatch(fetchLibrarySettings());
  }, [borrowDialogOpen, settings, dispatch]);

  const bookOptions = useMemo(() =>
    items
      .filter((item) => item.available_copies_count > 0)
      .map((item) => ({
        id: item.id,
        label: `${item.title}${item.author ? ` - ${item.author}` : ''} (${item.available_copies_count} متاح)`
      })),
  [items]);

  const borrowerOptions = useMemo(() =>
    borrowers.map((b) => ({ id: b.id, label: b.details ? `${b.name} - ${b.details}` : b.name })),
  [borrowers]);

  const dueDatePreview = useMemo(() => {
    const days = settings?.loan_days ?? 14;
    return formatDate(new Date(Date.now() + days * DAY));
  }, [settings]);

  const closeBorrowDialog = () => {
    setBorrowDialogOpen(false);
    setSelectedItem('');
    setSelectedBorrower('');
    setGuest(EMPTY_GUEST);
  };

  const handleBorrow = async () => {
    if (!selectedItem || (borrowerType !== 'other' && !selectedBorrower)) {
      showToast('يرجى اختيار الكتاب والمستعير', 'error');
      return;
    }

    const borrower = {
      student: { student_id: selectedBorrower },
      employee: { employee_id: selectedBorrower },
      other: { guest },
    }[borrowerType];

    try {
      await dispatch(borrowBook({ item_id: selectedItem, ...borrower })).unwrap();
      showToast('تمت الاستعارة بنجاح', 'success');
      closeBorrowDialog();
      refresh();
    } catch (error) {
      showToast(error.error || 'حدث خطأ', 'error');
    }
  };

  const handleReturn = async (transactionId) => {
    try {
      const result = await dispatch(returnBook(transactionId)).unwrap();
      const fine = Number(result.fine_amount || 0);
      showToast(fine > 0 ? `تم الإرجاع - غرامة تأخير: ${fine}` : 'تم الإرجاع بنجاح', fine > 0 ? 'warning' : 'success');
      refresh();
    } catch (error) {
      showToast(error.error || 'حدث خطأ', 'error');
    }
  };

  const matchesStatus = (t, status) =>
    status === 'all' || (status === 'overdue' ? isOverdue(t) : t.status === status);

  const statusOptions = useMemo(() => [
    { value: 'borrowed', label: 'مستعار الآن', count: transactions.filter((t) => t.status === 'borrowed').length },
    { value: 'overdue', label: 'متأخر', count: transactions.filter(isOverdue).length },
    { value: 'returned', label: 'تم الإرجاع', count: transactions.filter((t) => t.status === 'returned').length },
    { value: 'all', label: 'الكل', count: transactions.length },
  ], [transactions]);

  // Filter transactions
  const filteredTransactions = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return transactions.filter((transaction) => {
      const matchesSearch = !term ||
        (transaction.copy_title || '').toLowerCase().includes(term) ||
        (transaction.member_name || '').toLowerCase().includes(term) ||
        (transaction.member_phone || '').includes(term);

      let matchesDate = true;
      if (dateFilter !== 'all') {
        const transactionDate = new Date(transaction.borrowed_at);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (dateFilter === 'today') {
          matchesDate = transactionDate >= today;
        } else if (dateFilter === 'week') {
          matchesDate = transactionDate >= new Date(today.getTime() - 7 * DAY);
        } else if (dateFilter === 'month') {
          const monthAgo = new Date(today);
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          matchesDate = transactionDate >= monthAgo;
        }
      }

      return matchesSearch && matchesStatus(transaction, statusFilter) && matchesDate;
    });
  }, [transactions, searchTerm, statusFilter, dateFilter]);

  const handlePrint = () => {
    if (filteredTransactions.length === 0) {
      showToast('لا توجد بيانات للطباعة', 'info');
      return;
    }

    const printWindow = window.open('', '_blank');
    const statusLabel = statusOptions.find((o) => o.value === statusFilter)?.label;
    const filterText = [
      statusFilter !== 'all' && `الحالة: ${statusLabel}`,
      searchTerm && `البحث: ${escapeHtml(searchTerm)}`,
    ].filter(Boolean).join(' - ');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html dir="rtl">
        <head>
          <title>تقرير الاستعارة والإرجاع</title>
          <meta charset="UTF-8">
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;700;800&display=swap');
            body { font-family: 'Cairo', Arial, sans-serif; direction: rtl; padding: 24px; color: ${C.text}; }
            h1 { text-align: center; color: ${C.primary}; margin: 0 0 4px; }
            .meta { text-align: center; color: ${C.muted}; font-size: 13px; margin-bottom: 16px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border-bottom: 1px solid ${C.border}; padding: 10px; text-align: right; font-size: 13px; }
            th { background: ${C.primarySoft}; color: ${C.primary}; }
            .late { color: ${C.danger}; font-weight: bold; }
            .total { margin-top: 16px; font-weight: bold; }
          </style>
        </head>
        <body>
          <h1>تقرير الاستعارة والإرجاع</h1>
          <div class="meta">تاريخ الطباعة: ${formatDate(new Date())}${filterText ? ` · ${filterText}` : ''}</div>
          <table>
            <thead>
              <tr>
                <th>الكتاب</th><th>المستعير</th><th>تاريخ الاستعارة</th>
                <th>تاريخ الاستحقاق</th><th>الحالة</th><th>الغرامة</th>
              </tr>
            </thead>
            <tbody>
              ${filteredTransactions.map((t) => `
                <tr>
                  <td>${escapeHtml(t.copy_title || '-')}</td>
                  <td>${escapeHtml(t.member_name || '-')}</td>
                  <td>${formatDate(t.borrowed_at)}</td>
                  <td class="${isOverdue(t) ? 'late' : ''}">${formatDate(t.due_date)}</td>
                  <td>${timeStatus(t).label}</td>
                  <td>${Number(t.fine_amount) > 0 ? t.fine_amount : '-'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="total">إجمالي السجلات: ${filteredTransactions.length}</div>
        </body>
      </html>
    `);

    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  // Only block the tab on the first load - later refreshes keep the list visible
  if (loading && transactions.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress sx={{ color: C.primary }} />
      </Box>
    );
  }

  const newBorrowButton = (
    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setBorrowDialogOpen(true)} sx={primaryButtonSx}>
      استعارة جديدة
    </Button>
  );

  return (
    <Box>
      {transactions.length === 0 ? (
        <EmptyState
          icon={<SwapHorizIcon />}
          title="لا توجد استعارات بعد"
          hint="اختر كتاباً والمستعير، وسيُحسب موعد الإرجاع تلقائياً"
          action={newBorrowButton}
        />
      ) : (
        <>
          <Box display="flex" gap={1.5} mb={2} flexWrap="wrap" alignItems="center">
            <TextField
              placeholder="ابحث بالكتاب أو المستعير أو الهاتف..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: C.muted }} />
                  </InputAdornment>
                )
              }}
              sx={{ ...inputSx, minWidth: 220, flex: 1 }}
            />
            <TextField
              select
              size="small"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              sx={{ ...inputSx, minWidth: 130 }}
            >
              <MenuItem value="all">كل الفترات</MenuItem>
              <MenuItem value="today">اليوم</MenuItem>
              <MenuItem value="week">آخر أسبوع</MenuItem>
              <MenuItem value="month">آخر شهر</MenuItem>
            </TextField>
            <Tooltip title="طباعة">
              <IconButton onClick={handlePrint} sx={{ border: `1px solid ${C.border}`, borderRadius: 2.5, bgcolor: '#fff' }}>
                <PrintIcon />
              </IconButton>
            </Tooltip>
            {newBorrowButton}
          </Box>

          <Box mb={2}>
            <FilterChips options={statusOptions} value={statusFilter} onChange={setStatusFilter} />
          </Box>

          {filteredTransactions.length === 0 ? (
            <EmptyState icon={<SearchIcon />} title="لا توجد نتائج" hint="غيّر الفلتر أو كلمة البحث" />
          ) : (
            <Box display="flex" flexDirection="column" gap={1.25}>
              {filteredTransactions.map((transaction) => (
                <BorrowRow key={transaction.id} transaction={transaction} onReturn={handleReturn} saving={saving} />
              ))}
            </Box>
          )}
        </>
      )}

      <Dialog open={borrowDialogOpen} onClose={closeBorrowDialog} maxWidth="sm" fullWidth PaperProps={dialogPaperProps}>
        <DialogTitle sx={dialogTitleSx}>استعارة جديدة</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2.5} mt={1}>
            <Box>
              <Typography variant="body2" fontWeight={700} color={C.muted} mb={1} sx={{ fontFamily: FONT }}>
                ١. الكتاب
              </Typography>
              <SearchableSelect
                label="اختر الكتاب"
                options={bookOptions}
                value={selectedItem}
                onChange={setSelectedItem}
                noOptionsText="لا توجد كتب متاحة - أضف كتاباً أو نسخاً من تبويب الكتب"
                sx={inputSx}
              />
            </Box>

            <Box>
              <Typography variant="body2" fontWeight={700} color={C.muted} mb={1} sx={{ fontFamily: FONT }}>
                ٢. المستعير
              </Typography>
              <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={1} mb={1.5}>
                {BORROWER_TYPES.map((type) => {
                  const active = type.value === borrowerType;
                  return (
                    <ButtonBase
                      key={type.value}
                      onClick={() => setBorrowerType(type.value)}
                      sx={{
                        flexDirection: 'column', gap: 0.5, py: 1.25, borderRadius: 3,
                        fontFamily: FONT, fontWeight: 700,
                        border: `1.5px solid ${active ? C.primary : C.border}`,
                        bgcolor: active ? C.primarySoft : '#fff',
                        color: active ? C.primary : C.muted,
                        transition: 'all .15s',
                      }}
                    >
                      {type.icon}
                      {type.label}
                    </ButtonBase>
                  );
                })}
              </Box>

              {borrowerType === 'other' ? (
                <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={1.5}>
                  <TextField
                    label="الاسم (اختياري)"
                    value={guest.name}
                    onChange={(e) => setGuest({ ...guest, name: e.target.value })}
                    sx={{ ...inputSx, gridColumn: '1 / -1' }}
                  />
                  <TextField
                    label="رقم الهاتف (اختياري)"
                    value={guest.phone}
                    onChange={(e) => setGuest({ ...guest, phone: e.target.value })}
                    inputProps={{ inputMode: 'tel', dir: 'ltr' }}
                    sx={inputSx}
                  />
                  <TextField
                    label="رقم الهوية (اختياري)"
                    value={guest.id_number}
                    onChange={(e) => setGuest({ ...guest, id_number: e.target.value })}
                    inputProps={{ dir: 'ltr' }}
                    sx={inputSx}
                  />
                </Box>
              ) : (
                <SearchableSelect
                  label={borrowerType === 'student' ? 'اختر الطالب' : 'اختر الموظف'}
                  options={borrowerOptions}
                  value={selectedBorrower}
                  onChange={setSelectedBorrower}
                  sx={inputSx}
                />
              )}
            </Box>

            <Box
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                p: 1.5, borderRadius: 3, bgcolor: C.bg, border: `1px dashed ${C.border}`,
              }}
            >
              <Typography variant="body2" color={C.muted} sx={{ fontFamily: FONT }}>موعد الإرجاع</Typography>
              <Typography fontWeight={800} color={C.primary} sx={{ fontFamily: FONT }}>{dueDatePreview}</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeBorrowDialog} sx={{ fontFamily: FONT, color: C.muted }}>إلغاء</Button>
          <Button
            onClick={handleBorrow}
            variant="contained"
            disabled={saving || !selectedItem || (borrowerType !== 'other' && !selectedBorrower)}
            sx={primaryButtonSx}
          >
            {saving ? 'جاري المعالجة...' : 'تأكيد الاستعارة'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LibraryBorrowingTab;
